<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Table, Button, Badge, Spinner, EmptyState } from '@drama/ui'
import {
  Film,
  Sparkles,
  Search,
  X,
  Eye,
  List,
  Layers,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle2,
} from '@lucide/vue'

const props = defineProps({
  workspaceMode: { type: String, default: 'execution' },
  viewMode: { type: String, default: 'flat' },
  executionItems: { type: Array, default: () => [] },
  filteredExecutions: { type: Array, default: () => [] },
  groupedExecutions: { type: Array, default: () => [] },
  executionListLoading: { type: Boolean, default: false },
  executionListError: { type: String, default: '' },
  preparations: { type: Array, default: () => [] },
  filteredPreparations: { type: Array, default: () => [] },
  preparationLoading: { type: Boolean, default: false },
  preparationError: { type: String, default: '' },
  selectedFilm: { type: String, default: 'ALL' },
  filmOptions: { type: Array, default: () => [] },
  selectedStatusFilter: { type: String, default: 'ALL' },
  searchKeyword: { type: String, default: '' },
  directInputId: { type: String, default: '' },
  isFilterActive: { type: Boolean, default: false },
})

const emit = defineEmits([
  'update:workspaceMode',
  'update:viewMode',
  'update:selectedFilm',
  'update:selectedStatusFilter',
  'update:searchKeyword',
  'update:directInputId',
  'resetFilters',
  'directLookup',
  'openExecution',
  'openPreparation',
  'retryExecution',
  'retryPreparation',
])

const { t } = useI18n()

// 记录展开的单集 key 集合
const expandedEpisodes = ref(new Set())

function toggleEpisodeExpand(epKey) {
  if (expandedEpisodes.value.has(epKey)) {
    expandedEpisodes.value.delete(epKey)
  } else {
    expandedEpisodes.value.add(epKey)
  }
}

const statusOptions = computed(() => [
  { key: 'ALL', label: t('storyFilmReview.statusAll') },
  { key: 'READY', label: t('storyFilmReview.statusReady') },
  { key: 'PROCESSING', label: t('storyFilmReview.statusProcessing') },
  { key: 'FAILED', label: t('storyFilmReview.statusFailed') },
])

const executionTableColumns = computed(() => [
  { key: 'submission_id', label: '受理号与版本', width: '150px', mono: true },
  { key: 'film_title', label: '所属作品', width: '230px' },
  { key: 'episode', label: '季集规格', width: '160px' },
  { key: 'status', label: '成片状态', width: '170px' },
  { key: 'created_at', label: '提交时间', width: '170px' },
  { key: 'task_id', label: '任务号', width: '90px', mono: true },
  { key: 'actions', label: '操作', width: '110px', fixed: 'right' },
])

const preparationTableColumns = computed(() => [
  { key: 'preparation_id', label: '准备编号', width: '120px', mono: true },
  { key: 'film_title', label: '作品名称', width: '260px' },
  { key: 'preparation_scope', label: '范围', width: '120px' },
  { key: 'status', label: '状态', width: '140px' },
  { key: 'created_at', label: '提交时间', width: '170px' },
  { key: 'seq_range', label: '序列范围', width: '130px', mono: true },
  { key: 'actions', label: '操作', width: '110px', fixed: 'right' },
])

function statusTone(status) {
  if (status === 'failed' || status === 'rejected' || status === 'error') return 'danger'
  if (status === 'media_ready' || status === 'ok') return 'success'
  if (status === 'producing' || status === 'running') return 'info'
  return 'neutral'
}

function executionStatus(status) {
  const known = ['queued', 'producing', 'failed', 'review_ready', 'rejected', 'media_ready']
  return t(`storyFilm.executionLog.${known.includes(status) ? `status_${status}` : 'status_unknown'}`)
}
</script>

<template>
  <div class="story-film-list-view">
    <!-- 顶层主工作区切换与宽屏筛选条 -->
    <section class="global-command-bar">
      <!-- 模式 Tab 切换 -->
      <div class="workspace-mode-tabs">
        <button
          type="button"
          class="ws-tab-btn"
          :class="{ 'is-active': workspaceMode === 'execution' }"
          @click="emit('update:workspaceMode', 'execution')"
        >
          <Film :size="15" />
          <span>{{ t('storyFilmReview.submittedTasks') }}</span>
          <Badge size="xs" :variant="workspaceMode === 'execution' ? 'info' : 'neutral'">{{ executionItems.length }}</Badge>
        </button>
        <button
          type="button"
          class="ws-tab-btn"
          :class="{ 'is-active': workspaceMode === 'independent' }"
          @click="emit('update:workspaceMode', 'independent')"
        >
          <Sparkles :size="15" />
          <span>{{ t('storyFilmReview.preparationWorkspace') }}</span>
          <Badge size="xs" :variant="workspaceMode === 'independent' ? 'info' : 'neutral'">{{ preparations.length }}</Badge>
        </button>
      </div>

      <!-- 全局筛选控制组 -->
      <div class="global-filters-row">
        <!-- 视图模式切换 (平铺时间流 vs 剧集聚合视图，仅受理制作模式) -->
        <div v-if="workspaceMode === 'execution'" class="view-mode-toggle">
          <button
            type="button"
            class="view-toggle-btn"
            :class="{ 'is-active': viewMode === 'flat' }"
            title="平铺时间流水列表"
            @click="emit('update:viewMode', 'flat')"
          >
            <List :size="14" />
            <span>时间流水</span>
          </button>
          <button
            type="button"
            class="view-toggle-btn"
            :class="{ 'is-active': viewMode === 'grouped' }"
            title="按剧目与单集聚合查看"
            @click="emit('update:viewMode', 'grouped')"
          >
            <Layers :size="14" />
            <span>按剧集聚合</span>
          </button>
        </div>

        <!-- 作品筛选下拉框 -->
        <select
          :value="selectedFilm"
          class="global-film-select"
          @change="emit('update:selectedFilm', $event.target.value)"
        >
          <option value="ALL">全部作品 ({{ workspaceMode === 'execution' ? executionItems.length : preparations.length }})</option>
          <option v-for="film in filmOptions" :key="film.title" :value="film.title">
            {{ film.title }} ({{ film.count }})
          </option>
        </select>

        <!-- 状态分段筛选 (仅受理制作模式) -->
        <div v-if="workspaceMode === 'execution'" class="status-segmented-group">
          <button
            v-for="st in statusOptions"
            :key="st.key"
            type="button"
            class="seg-btn"
            :class="{ 'is-active': selectedStatusFilter === st.key }"
            @click="emit('update:selectedStatusFilter', st.key)"
          >
            {{ st.label }}
          </button>
        </div>

        <!-- 关键词搜索框 -->
        <div class="global-search-box">
          <Search :size="14" class="search-ico" />
          <input
            :value="searchKeyword"
            type="search"
            class="global-search-inp"
            :placeholder="t('storyFilm.executionLog.searchPlaceholder')"
            @input="emit('update:searchKeyword', $event.target.value)"
          />
          <button
            v-if="searchKeyword"
            type="button"
            class="clear-search-btn"
            @click="emit('update:searchKeyword', '')"
          >
            <X :size="13" />
          </button>
        </div>

        <!-- 受理号直达 -->
        <form
          v-if="workspaceMode === 'execution'"
          class="quick-jump-form"
          @submit.prevent="emit('directLookup')"
        >
          <input
            :value="directInputId"
            class="quick-jump-inp"
            placeholder="直达受理号"
            inputmode="numeric"
            @input="emit('update:directInputId', $event.target.value)"
          />
          <Button
            size="sm"
            variant="secondary"
            :disabled="!/^\d+$/.test(directInputId.trim())"
            type="submit"
          >
            {{ t('storyFilm.executionLog.load') }}
          </Button>
        </form>

        <button
          v-if="isFilterActive"
          type="button"
          class="reset-filter-btn"
          @click="emit('resetFilters')"
        >
          {{ t('storyFilmReview.resetFilters') }}
        </button>
      </div>
    </section>

    <!-- 列表主展示区 -->
    <main class="table-container-card">
      <!-- A. 制作执行任务视图 (受理制作) -->
      <template v-if="workspaceMode === 'execution'">
        <div v-if="executionListLoading && !executionItems.length" class="center-state">
          <Spinner />
          <span>{{ t('storyFilm.executionLog.recordLoading') }}</span>
        </div>
        <div v-else-if="executionListError" class="center-state">
          <p class="error-msg">{{ executionListError }}</p>
          <Button variant="secondary" @click="emit('retryExecution')">重试</Button>
        </div>
        <EmptyState v-else-if="!filteredExecutions.length" title="暂无匹配制作任务" />

        <!-- 模式 1: 时间流水平铺 Table 视图 (高区分度、丰富时间与版本维度) -->
        <Table
          v-else-if="viewMode === 'flat'"
          :columns="executionTableColumns"
          :rows="filteredExecutions"
          row-key="submission_id"
          clickable
          @row-click="row => emit('openExecution', row.submission_id)"
        >
          <!-- 1. 受理号与版本标记 -->
          <template #submission_id="{ row }">
            <div class="sub-cell">
              <span class="sub-number">#{{ row.submission_id }}</span>
              <Badge v-if="row.is_latest" size="xs" variant="primary">最新产出</Badge>
              <span v-else class="history-tag">历史迭代</span>
            </div>
          </template>

          <!-- 2. 所属作品 -->
          <template #film_title="{ row }">
            <div class="film-cell">
              <div class="film-row">
                <Film :size="14" class="film-icon" />
                <span class="film-name">{{ row.film_title || '未命名作品' }}</span>
              </div>
              <span v-if="row.episode_total_count > 1" class="film-sub">
                本集累计 {{ row.episode_total_count }} 次制作
              </span>
            </div>
          </template>

          <!-- 3. 季集规格 -->
          <template #episode="{ row }">
            <div class="episode-cell">
              <span class="episode-pill">S{{ row.season_no ?? 1 }} · E{{ row.episode_no ?? 1 }}</span>
              <span class="episode-label">第 {{ row.season_no ?? 1 }} 季 第 {{ row.episode_no ?? 1 }} 集</span>
            </div>
          </template>

          <!-- 4. 成片状态 -->
          <template #status="{ row }">
            <div class="status-cell">
              <Badge size="sm" :variant="statusTone(row.status)">{{ executionStatus(row.status) }}</Badge>
              <span v-if="row.is_active_media" class="active-badge">
                <CheckCircle2 :size="11" />
                <span>当前生效成片</span>
              </span>
            </div>
          </template>

          <!-- 5. 提交时间 (极大提升时间流区分度) -->
          <template #created_at="{ row }">
            <div class="time-cell">
              <span class="time-primary">{{ row.time_ago }}</span>
              <span class="time-secondary">{{ row.formatted_time }}</span>
            </div>
          </template>

          <!-- 6. 任务号 -->
          <template #task_id="{ row }">
            <span class="task-cell">{{ row.task_id ? `#${row.task_id}` : '—' }}</span>
          </template>

          <!-- 7. 操作 -->
          <template #actions="{ row }">
            <Button size="sm" variant="secondary" @click.stop="emit('openExecution', row.submission_id)">
              <span>核对分镜</span>
              <Eye :size="13" />
            </Button>
          </template>
        </Table>

        <!-- 模式 2: 按剧集聚合 Grouped 视图 (彻底解决同一剧集多次提交混乱的问题) -->
        <div v-else class="grouped-view-container">
          <div v-for="film in groupedExecutions" :key="film.film_title" class="film-group-card">
            <header class="film-group-header">
              <div class="film-group-header__left">
                <Film :size="16" class="film-ico-lg" />
                <h3 class="film-group-title">{{ film.film_title }}</h3>
                <Badge size="xs" variant="neutral">共 {{ film.total_submissions }} 条制作记录</Badge>
              </div>
            </header>

            <div class="episode-cards-grid">
              <div
                v-for="ep in film.episodes"
                :key="ep.episode_key"
                class="episode-card"
              >
                <!-- 单集头部概览 -->
                <div class="episode-card-header">
                  <div class="ep-header-left">
                    <span class="ep-capsule">S{{ ep.season_no }} · E{{ ep.episode_no }}</span>
                    <span class="ep-text">第 {{ ep.season_no }} 季 · 第 {{ ep.episode_no }} 集</span>
                    <Badge size="xs" variant="neutral">共 {{ ep.items.length }} 次迭代</Badge>
                  </div>
                  <div class="ep-header-right">
                    <Badge size="sm" :variant="statusTone(ep.latest_item.status)">
                      {{ executionStatus(ep.latest_item.status) }}
                    </Badge>
                    <Button
                      size="sm"
                      variant="primary"
                      @click="emit('openExecution', ep.latest_item.submission_id)"
                    >
                      <span>核对最新 (#{{ ep.latest_item.submission_id }})</span>
                      <Eye :size="13" />
                    </Button>
                  </div>
                </div>

                <!-- 最新版本简报 -->
                <div class="latest-summary-bar">
                  <div class="summary-field">
                    <span class="summary-lbl">最新提交：</span>
                    <b class="summary-val">#{{ ep.latest_item.submission_id }}</b>
                    <span class="summary-time">({{ ep.latest_item.time_ago }})</span>
                  </div>
                  <div v-if="ep.active_item && ep.active_item.submission_id !== ep.latest_item.submission_id" class="summary-field">
                    <span class="summary-lbl">当前生效：</span>
                    <span class="summary-val text-success">#{{ ep.active_item.submission_id }} 可播放</span>
                  </div>
                  <button
                    v-if="ep.items.length > 1"
                    type="button"
                    class="toggle-history-btn"
                    @click="toggleEpisodeExpand(ep.episode_key)"
                  >
                    <span>{{ expandedEpisodes.has(ep.episode_key) ? '收起历史版本' : `查看全部 ${ep.items.length} 次版本` }}</span>
                    <component :is="expandedEpisodes.has(ep.episode_key) ? ChevronDown : ChevronRight" :size="14" />
                  </button>
                </div>

                <!-- 展开的历史版本列表 -->
                <div v-if="expandedEpisodes.has(ep.episode_key)" class="history-version-timeline">
                  <div
                    v-for="sub in ep.items"
                    :key="sub.submission_id"
                    class="history-version-row"
                    :class="{ 'is-latest-row': sub.is_latest, 'is-active-row': sub.is_active_media }"
                    @click="emit('openExecution', sub.submission_id)"
                  >
                    <div class="ver-col-id">
                      <b>#{{ sub.submission_id }}</b>
                      <Badge v-if="sub.is_latest" size="xs" variant="primary">最新</Badge>
                      <span v-if="sub.is_active_media" class="ver-active-tag">生效成片</span>
                    </div>
                    <div class="ver-col-status">
                      <Badge size="xs" :variant="statusTone(sub.status)">{{ executionStatus(sub.status) }}</Badge>
                    </div>
                    <div class="ver-col-time">
                      <span class="time-primary">{{ sub.time_ago }}</span>
                      <span class="time-secondary">{{ sub.formatted_time }}</span>
                    </div>
                    <div class="ver-col-task">
                      <span>任务 #{{ sub.task_id || '—' }}</span>
                    </div>
                    <div class="ver-col-action">
                      <Button size="xs" variant="secondary" @click.stop="emit('openExecution', sub.submission_id)">
                        <span>核对</span>
                        <Eye :size="12" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- B. 样片准备任务列表 Table (补充时间与状态区分) -->
      <template v-else>
        <div v-if="preparationLoading && !preparations.length" class="center-state">
          <Spinner />
          <span>{{ t('storyFilmReview.loading') }}</span>
        </div>
        <div v-else-if="preparationError" class="center-state">
          <p class="error-msg">{{ preparationError }}</p>
          <Button variant="secondary" @click="emit('retryPreparation')">重试</Button>
        </div>
        <EmptyState v-else-if="!filteredPreparations.length" title="暂无匹配样片记录" />
        <Table
          v-else
          :columns="preparationTableColumns"
          :rows="filteredPreparations"
          row-key="preparation_id"
          clickable
          @row-click="row => emit('openPreparation', row)"
        >
          <template #preparation_id="{ row }"><b>#{{ row.preparation_id }}</b></template>
          <template #film_title="{ row }"><b>{{ row.film_title || '样片记录 #' + row.preparation_id }}</b></template>
          <template #preparation_scope="{ row }">
            <span class="scope-pill">{{ row.preparation_scope || '全局' }}</span>
          </template>
          <template #status="{ row }">
            <Badge size="sm" :variant="statusTone(row.status)">{{ row.status }}</Badge>
          </template>
          <template #created_at="{ row }">
            <div class="time-cell">
              <span class="time-primary">{{ row.time_ago }}</span>
              <span class="time-secondary">{{ row.formatted_time }}</span>
            </div>
          </template>
          <template #seq_range="{ row }">{{ row.from_seq != null ? `${row.from_seq} → ${row.through_seq}` : '—' }}</template>
          <template #actions="{ row }">
            <Button size="sm" variant="secondary" @click.stop="emit('openPreparation', row)">
              <span>审查档案</span>
              <Eye :size="13" />
            </Button>
          </template>
        </Table>
      </template>
    </main>
  </div>
</template>

<style scoped>
.story-film-list-view {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  width: 100%;
}

.global-command-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  flex-wrap: wrap;
  padding: 10px 14px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  box-shadow: var(--drama-shadow-xs);
}

.workspace-mode-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--drama-color-surface-alt);
  padding: 4px;
  border-radius: var(--drama-radius-sm);
  border: 1px solid var(--drama-color-border);
}

.ws-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  font-size: var(--drama-font-size-sm);
  font-weight: 500;
  border: none;
  background: transparent;
  color: var(--drama-color-text-muted);
  border-radius: var(--drama-radius-xs, 4px);
  cursor: pointer;
  transition: all 0.15s ease;
}

.ws-tab-btn:hover {
  color: var(--drama-color-text);
}

.ws-tab-btn.is-active {
  background: var(--drama-color-surface);
  color: var(--drama-color-text);
  font-weight: 600;
  box-shadow: var(--drama-shadow-xs);
}

.global-filters-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;
}

/* 视图切换：时间流水 vs 按剧集聚合 */
.view-mode-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--drama-color-surface-alt);
  padding: 3px;
  border-radius: var(--drama-radius-sm);
  border: 1px solid var(--drama-color-border);
}

.view-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: var(--drama-font-size-xs);
  border: none;
  background: transparent;
  color: var(--drama-color-text-muted);
  border-radius: var(--drama-radius-xs, 3px);
  cursor: pointer;
  transition: all 0.15s ease;
}

.view-toggle-btn.is-active {
  background: var(--drama-color-surface);
  color: var(--drama-color-text);
  font-weight: 600;
  box-shadow: var(--drama-shadow-xs);
}

.global-film-select {
  padding: 6px 12px;
  font-size: var(--drama-font-size-sm);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
  min-width: 170px;
  cursor: pointer;
}

.status-segmented-group {
  display: flex;
  align-items: center;
  background: var(--drama-color-surface-alt);
  padding: 3px;
  border-radius: var(--drama-radius-sm);
  border: 1px solid var(--drama-color-border);
}

.seg-btn {
  padding: 4px 10px;
  font-size: var(--drama-font-size-xs);
  border: none;
  background: transparent;
  color: var(--drama-color-text-muted);
  border-radius: var(--drama-radius-xs, 3px);
  cursor: pointer;
  white-space: nowrap;
}

.seg-btn.is-active {
  background: var(--drama-color-surface);
  color: var(--drama-color-text);
  font-weight: 600;
  box-shadow: var(--drama-shadow-xs);
}

.global-search-box {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 200px;
}

.search-ico {
  position: absolute;
  left: 10px;
  color: var(--drama-color-text-muted);
  pointer-events: none;
}

.global-search-inp {
  width: 100%;
  padding: 6px 26px 6px 30px;
  font-size: var(--drama-font-size-sm);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
}

.global-search-inp:focus {
  outline: none;
  border-color: var(--drama-color-primary);
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  color: var(--drama-color-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
}

.quick-jump-form {
  display: flex;
  align-items: center;
  gap: 6px;
}

.quick-jump-inp {
  width: 90px;
  padding: 5px 8px;
  font-size: var(--drama-font-size-sm);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
}

.reset-filter-btn {
  border: none;
  background: transparent;
  color: var(--drama-color-info);
  font-size: var(--drama-font-size-xs);
  cursor: pointer;
  text-decoration: underline;
}

.table-container-card {
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  overflow: hidden;
  box-shadow: var(--drama-shadow-xs);
  min-height: 480px;
}

/* 表格单元格定制样式 */
.sub-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sub-number {
  font-weight: 700;
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text);
}

.history-tag {
  font-size: 11px;
  color: var(--drama-color-text-muted);
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  padding: 1px 6px;
  border-radius: 10px;
  white-space: nowrap;
}

.film-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.film-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.film-icon {
  color: var(--drama-color-primary);
  flex-shrink: 0;
}

.film-name {
  font-weight: 600;
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-sm);
}

.film-sub {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  padding-left: 20px;
}

.episode-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.episode-pill {
  display: inline-block;
  padding: 2px 8px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: 4px;
  font-weight: 600;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text);
  width: fit-content;
}

.episode-label {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.active-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: var(--drama-color-success);
}

.time-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.time-primary {
  font-size: var(--drama-font-size-sm);
  font-weight: 600;
  color: var(--drama-color-text);
}

.time-secondary {
  font-size: 11px;
  color: var(--drama-color-text-muted);
  font-family: monospace;
}

.task-cell {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.scope-pill {
  display: inline-block;
  padding: 2px 8px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: 4px;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text);
}

/* 剧集聚合视图 (Grouped View) 样式 */
.grouped-view-container {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-lg);
  padding: var(--drama-space-lg);
}

.film-group-card {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  padding: 16px;
}

.film-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.film-group-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.film-ico-lg {
  color: var(--drama-color-primary);
}

.film-group-title {
  margin: 0;
  font-size: var(--drama-font-size-md);
  font-weight: 700;
  color: var(--drama-color-text);
}

.episode-cards-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.episode-card {
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  overflow: hidden;
  box-shadow: var(--drama-shadow-xs);
}

.episode-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
}

.ep-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ep-capsule {
  display: inline-block;
  padding: 3px 8px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: 4px;
  font-weight: 700;
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text);
}

.ep-text {
  font-size: var(--drama-font-size-sm);
  font-weight: 600;
  color: var(--drama-color-text);
}

.ep-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.latest-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: var(--drama-color-surface-alt);
  font-size: var(--drama-font-size-xs);
}

.summary-field {
  display: flex;
  align-items: center;
  gap: 6px;
}

.summary-lbl {
  color: var(--drama-color-text-muted);
}

.summary-val {
  color: var(--drama-color-text);
}

.summary-val.text-success {
  color: var(--drama-color-success);
  font-weight: 600;
}

.summary-time {
  color: var(--drama-color-text-muted);
}

.toggle-history-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: var(--drama-color-info);
  font-size: var(--drama-font-size-xs);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--drama-radius-xs);
}

.toggle-history-btn:hover {
  background: var(--drama-color-surface);
}

.history-version-timeline {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
}

.history-version-row {
  display: grid;
  grid-template-columns: 140px 140px 180px 120px 1fr;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--drama-color-border-subtle, #f0ede8);
  font-size: var(--drama-font-size-xs);
  cursor: pointer;
  transition: background 0.12s ease;
}

.history-version-row:last-child {
  border-bottom: none;
}

.history-version-row:hover {
  background: var(--drama-color-surface-alt);
}

.history-version-row.is-latest-row {
  background: rgba(var(--drama-rgb-primary, 245, 158, 11), 0.04);
}

.ver-col-id {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ver-active-tag {
  font-size: 10px;
  color: var(--drama-color-success);
  background: rgba(34, 197, 94, 0.1);
  padding: 1px 4px;
  border-radius: 3px;
  font-weight: 600;
}

.ver-col-time {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ver-col-task {
  color: var(--drama-color-text-muted);
}

.ver-col-action {
  display: flex;
  justify-content: flex-end;
}

.center-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--drama-space-md);
  min-height: 380px;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.error-msg {
  color: var(--drama-color-danger);
  margin: 0;
}
</style>
